import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, Maximize2, Minimize2, ArrowLeft, Users, Target, Building2, Coins } from 'lucide-react';

const SYMBOLS = ['♦', '♠', '♣', '7', '◆', '★'];
const COLORS = ['#FF204E', '#B026FF', '#E0B85A', '#7A18FF', '#D90035', '#2FBF71'];
// Deterministic demo outcomes (no real gambling maths)
const DEMO_OUTCOMES = [
  [0, 0, 0], [1, 3, 5], [2, 2, 4], [4, 4, 4], [3, 1, 0], [5, 5, 2],
];

export default function ProviderGame() {
  const nav = useNavigate();
  const [focus, setFocus] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [reels, setReels] = useState([0, 1, 2]);
  const [outcomeIdx, setOutcomeIdx] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [msg, setMsg] = useState('Place your stake and spin.');
  const spinRef = useRef(null);

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setMsg('Provider RNG resolving...');
    let ticks = 0;
    spinRef.current = setInterval(() => {
      setReels([Math.floor(Math.random() * 6), Math.floor(Math.random() * 6), Math.floor(Math.random() * 6)]);
      ticks++;
      if (ticks > 14) {
        clearInterval(spinRef.current);
        const out = DEMO_OUTCOMES[outcomeIdx % DEMO_OUTCOMES.length];
        setReels(out);
        const win = out[0] === out[1] && out[1] === out[2];
        setMsg(win ? 'Demo win — outcome supplied by provider RNG.' : 'No win this round. (Demo outcome)');
        setOutcomeIdx((i) => i + 1);
        setSpinning(false);
      }
    }, 90);
  };
  useEffect(() => () => clearInterval(spinRef.current), []);

  return (
    <div className="min-h-screen bg-[#070609] flex flex-col">
      {/* Minimal Xincity toolbar */}
      <div className="h-14 border-b border-white/5 xc-glass flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-4">
          <button onClick={() => nav('/casino')} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> Casino</button>
          <span className="text-white/20">|</span>
          <span className="font-display font-700 tracking-[0.18em] text-white text-sm">XINCITY<sup className="text-[0.5em] align-super text-white/70">™</sup></span>
          <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#B026FF]/40 text-[#B026FF]">FOCUS MODE {focus ? 'ON' : 'OFF'}</span>
        </div>
        <div className="flex items-center gap-2">
          {!focus && (
            <div className="hidden md:flex items-center gap-1">
              <button onClick={() => nav('/city')} className="btn-ghost rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> City</button>
              <button onClick={() => nav('/crew')} className="btn-ghost rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Crew</button>
              <button onClick={() => nav('/missions')} className="btn-ghost rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5"><Target className="h-3.5 w-3.5" /> Missions</button>
            </div>
          )}
          <button onClick={() => setFocus((v) => !v)} className="btn-ghost rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5">
            {focus ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />} Focus Mode
          </button>
          <button onClick={() => nav('/city')} className="btn-neon rounded-md px-4 py-1.5 text-xs font-600">RETURN TO XINCITY</button>
        </div>
      </div>

      <div className={`flex-1 grid ${focus ? 'grid-cols-1' : 'lg:grid-cols-[1fr_320px]'}`}>
        {/* Game stage */}
        <div className="relative flex items-center justify-center p-6 overflow-hidden">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(80% 70% at 50% 30%, rgba(122,24,255,0.22), transparent 60%), radial-gradient(70% 60% at 50% 90%, rgba(255,32,78,0.18), transparent 60%)' }} />
          <div className="absolute inset-0 xc-grid opacity-20" />
          <div className="relative w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="font-display font-700 text-xl text-white">Crown of Fortune</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/50">Provider Game Demo</span>
              </div>
              <button onClick={() => setShowInfo((v) => !v)} className="h-9 w-9 grid place-items-center rounded-md border border-white/10 bg-white/5"><Info className="h-4 w-4 text-white/70" /></button>
            </div>

            {/* Reels */}
            <div className="rounded-2xl p-6 xc-glass border border-[#FF204E]/20">
              <div className="grid grid-cols-3 gap-4">
                {reels.map((s, i) => (
                  <div key={i} className="aspect-square rounded-xl grid place-items-center overflow-hidden relative"
                    style={{ background: 'linear-gradient(160deg,#151119,#0D0B10)', border: `1px solid ${COLORS[s]}55` }}>
                    <span className={`font-display font-700 text-5xl md:text-6xl ${spinning ? 'blur-[1px]' : ''}`} style={{ color: COLORS[s], textShadow: `0 0 26px ${COLORS[s]}` }}>{SYMBOLS[s]}</span>
                  </div>
                ))}
              </div>
              {showInfo && (
                <div className="mt-4 text-xs text-white/50 leading-relaxed rounded-lg p-3 bg-white/[0.03] border border-white/10">
                  This is a fictional demo interface. In production, reels, mathematics, RTP, RNG, paylines and outcomes are controlled entirely by the licensed game provider. Xincity never alters game results.
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="rounded-lg px-4 py-2.5 bg-white/[0.04] border border-white/10"><div className="text-[10px] text-white/40 uppercase">Balance</div><div className="text-white font-600">€183.40</div></div>
                <div className="rounded-lg px-4 py-2.5 bg-white/[0.04] border border-white/10"><div className="text-[10px] text-white/40 uppercase">Stake</div><div className="text-white font-600">€1.00</div></div>
              </div>
              <button onClick={spin} disabled={spinning}
                className="btn-neon rounded-xl px-10 py-4 font-700 tracking-widest disabled:opacity-60 disabled:cursor-not-allowed">
                {spinning ? 'SPINNING...' : 'SPIN'}
              </button>
            </div>
            <div className="mt-3 text-center text-sm text-white/55">{msg}</div>
          </div>
        </div>

        {/* Side rail when not focus */}
        {!focus && (
          <aside className="border-l border-white/5 bg-[#0D0B10] p-5 hidden lg:block">
            <div className="text-xs tracking-[0.2em] uppercase text-white/40 mb-3">Xincity is optional</div>
            <p className="text-sm text-white/55 leading-relaxed">Turn on Focus Mode to remove all Xincity elements and play the casino game completely undistracted. No crew chat, no mission pressure.</p>
            <div className="mt-5 space-y-3">
              <div className="rounded-lg p-4 bg-white/[0.03] border border-white/8"><div className="flex items-center gap-2 text-sm text-white/80"><Coins className="h-4 w-4 text-[#E0B85A]" /> Casino play still counts toward Xincity progress — only if you choose to engage.</div></div>
              <button onClick={() => nav('/missions/loyalty-job')} className="btn-ghost rounded-lg w-full py-3 text-sm font-500">View active crew mission</button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
